import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon, type IconName } from '@/icons';
import { usePreferences } from '@/hooks/usePreferences';
import { PLATFORM_LABELS, releaseDate, type UpdateRelease } from '@/core/data/updateLogs';
import { BannerArtwork } from './BannerArtwork';
import { PlatformIcon } from './PlatformIcon';
import { Control, focusRing } from './styles';

const enterRight = keyframes`from { transform: translateX(-100%); } to { transform: translateX(0); }`;
const leaveRight = keyframes`from { transform: translateX(0); } to { transform: translateX(100%); }`;
const Frame = styled.section`
  --banner-text: ${({ theme }) => (theme.mode === 'dark' ? '#f3edff' : '#27153f')};
  --banner-muted: ${({ theme }) => (theme.mode === 'dark' ? '#c4b8de' : '#635175')};
  --banner-accent: ${({ theme }) => (theme.mode === 'dark' ? '#c895ff' : '#7134a8')};
  --banner-link: ${({ theme }) => (theme.mode === 'dark' ? '#d4a0ff' : '#7130aa')};
  --banner-chip: ${({ theme }) => (theme.mode === 'dark' ? '#dfb6ff' : '#6b2ca0')};
  --banner-chip-bg: ${({ theme }) => (theme.mode === 'dark' ? '#682bd34d' : '#b880ef24')};
  --banner-icon: ${({ theme }) => (theme.mode === 'dark' ? '#c08afc' : '#8950c3')};
  --banner-android: ${({ theme }) => (theme.mode === 'dark' ? '#9edf81' : '#417f27')};
  --banner-web: ${({ theme }) => (theme.mode === 'dark' ? '#bbceff' : '#4b60a1')};
  --banner-node: ${({ theme }) => (theme.mode === 'dark' ? '#241c40' : '#eee3fb')};
  --banner-dot: ${({ theme }) => (theme.mode === 'dark' ? '#71618d' : '#b6a4ce')};
  --banner-art-from: ${({ theme }) => (theme.mode === 'dark' ? '#7944d8' : '#dfc8fc')};
  --banner-art-mid: ${({ theme }) => (theme.mode === 'dark' ? '#302366' : '#ccb5ed')};
  --banner-art-to: ${({ theme }) => (theme.mode === 'dark' ? '#14132d' : '#ede3fc')};
  --banner-art-text: ${({ theme }) => (theme.mode === 'dark' ? '#f2eaff' : '#482475')};
  --banner-mountain-back: ${({ theme }) => (theme.mode === 'dark' ? '#27204e' : '#daccf0')};
  --banner-mountain-front: ${({ theme }) => (theme.mode === 'dark' ? '#17152f' : '#eee5fa')};
  --banner-path: ${({ theme }) => (theme.mode === 'dark' ? '#473078' : '#b59bd8')};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  color: var(--banner-text);
  background:
    radial-gradient(
      ellipse at 95% 0%,
      ${({ theme }) => (theme.mode === 'dark' ? '#39206688' : '#c7a2f54d')},
      transparent 65%
    ),
    linear-gradient(
      120deg,
      ${({ theme }) =>
        theme.mode === 'dark' ? '#241840, #131329 65%, #171332' : '#ece0fc, #faf7ff 65%, #eee5fc'}
    );
`;
// Every slide contributes to the same grid cell's height, including hidden slides.
// This prevents layout jumps without clipping translated or enlarged text.
const Viewport = styled.div`
  display: grid;
`;
const Slide = styled.div<{ $leaving: boolean; $animate: boolean; $hidden: boolean }>`
  display: grid;
  grid-area: 1 / 1;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  grid-template-columns: minmax(160px, 0.85fr) minmax(210px, 1.6fr) minmax(150px, 0.95fr);
  align-items: center;
  min-height: 215px;
  gap: 24px;
  padding: 22px 22px 4px 28px;
  position: relative;
  animation: ${({ $animate, $leaving }) =>
      $animate ? ($leaving ? leaveRight : enterRight) : 'none'}
    520ms cubic-bezier(0.22, 0.7, 0.2, 1) both;
  @media (max-width: 1200px) {
    gap: 18px;
    grid-template-columns: minmax(145px, 0.9fr) minmax(200px, 1.6fr) minmax(130px, 0.9fr);
    padding-left: 22px;
  }
  @media (max-width: 850px) {
    grid-template-columns: 155px minmax(0, 1fr);
    min-height: 280px;
    padding-bottom: 12px;
    & > div:last-child {
      display: none;
    }
  }
  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
    min-height: 350px;
    align-content: start;
    padding: 22px 20px 6px;
    & > div:last-child {
      display: flex;
      position: absolute;
      top: 20px;
      right: 8px;
      width: 108px;
      height: 135px;
      opacity: 0.8;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const Metadata = styled.div`
  align-self: center;
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
  padding-right: 18px;
  & > strong {
    display: block;
    margin: 9px 0 5px;
    color: var(--banner-accent);
    font-size: clamp(1.65rem, 2.6vw, 2.65rem);
    letter-spacing: -0.04em;
    font-weight: 750;
    white-space: nowrap;
  }
  & > p {
    margin: 0 0 13px;
    color: var(--banner-muted);
    font-size: 0.75rem;
    line-height: 1.5;
  }
  @media (max-width: 520px) {
    border: 0;
    padding: 0;
    width: calc(100% - 90px);
    & > strong {
      font-size: 2rem;
      margin: 6px 0 2px;
    }
    & > p {
      margin-bottom: 8px;
    }
  }
`;
const Latest = styled.span`
  display: inline-block;
  padding: 5px 11px;
  border: 1px solid transparent;
  border-radius: 7px;
  font-size: 0.7rem;
  color: var(--banner-chip);
  background: var(--banner-chip-bg);
  font-weight: 600;
`;
const Platforms = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  & > span {
    display: flex;
    gap: 6px;
    align-items: center;
    padding: 5px 9px;
    border-radius: 20px;
    background: #a193dc1f;
    border: 1px solid transparent;
    font-size: 0.7rem;
  }
  & [data-platform='android'] svg {
    color: var(--banner-android);
  }
  & [data-platform='web'] svg {
    color: var(--banner-web);
  }
`;
const Message = styled.div`
  align-self: center;
  min-width: 0;
  & h2 {
    font-size: 1rem;
    line-height: 1.4;
    margin: 0 0 10px;
    font-weight: 600;
  }
  & p {
    font-size: 0.8rem;
    line-height: 1.7;
    margin: 0;
    color: var(--banner-muted);
  }
`;
const Highlights = styled.div<{ $timeline?: boolean }>`
  position: relative;
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 9px;
  margin-top: 18px;
  & > span {
    z-index: 1;
    display: flex;
    flex-direction: ${({ $timeline }) => ($timeline ? 'column' : 'row')};
    align-items: center;
    gap: 9px;
    color: var(--banner-text);
    font-size: 0.65rem;
    line-height: 1.5;
    flex: 1;
  }
  & svg {
    color: var(--banner-icon);
    flex-shrink: 0;
  }
  ${({ $timeline }) =>
    $timeline
      ? `
    &::before { content: ''; position: absolute; left: 15%; right: 15%; top: 16px; height: 1px; background: #694cae; }
    & svg { padding: 8px; box-sizing: content-box; border: 1px solid transparent; border-radius: 50%; background: color-mix(in srgb, #a77df0 18%, var(--banner-node)); }
    & > span { text-align: center; }
  `
      : ''}
`;
const CommunityHighlights = styled(Highlights)`
  & > span {
    background: #ba95ff0b;
    border: 1px solid transparent;
    border-radius: 8px;
    padding: 9px 6px;
  }
`;
const ReleaseLink = styled.button`
  ${focusRing}
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 18px 0 0;
  padding: 5px 0;
  color: var(--banner-link);
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  &:hover {
    color: var(--banner-accent);
    text-decoration: underline;
  }
`;
const Controls = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  min-height: 38px;
  padding: 0 15px 9px;
  & > span {
    margin-right: auto;
    font-size: 0.65rem;
    color: var(--banner-muted);
    padding-left: 12px;
  }
`;
const BannerControl = styled(Control)`
  min-height: 30px;
  min-width: 30px;
  padding: 5px;
  border: 0;
  background: transparent;
  color: var(--banner-link);
  &:hover {
    background: #aa88dd22;
  }
`;
const Dot = styled(BannerControl)<{ $selected: boolean }>`
  min-width: 22px;
  &::after {
    content: '';
    display: block;
    width: ${({ $selected }) => ($selected ? '17px' : '5px')};
    height: 5px;
    border-radius: 5px;
    background: ${({ $selected }) => ($selected ? 'var(--banner-accent)' : 'var(--banner-dot)')};
  }
`;
const SLIDE_NAMES = ['Product focus', 'What’s new', 'Release timeline', 'Built with our community'];
const HIGHLIGHTS: [IconName, string][][] = [
  [],
  [
    ['comment', 'Better conversations'],
    ['users', 'Useful connections'],
    ['shield', 'Clearer controls'],
  ],
  [
    ['sparkles', 'New features'],
    ['gauge', 'Ongoing improvements'],
    ['shield', 'Security updates'],
  ],
  [
    ['users', 'You suggest'],
    ['settings', 'We build'],
    ['heart', 'A stronger community'],
  ],
];

export function ReleaseBanner({
  release,
  onViewRelease,
}: {
  release: UpdateRelease;
  onViewRelease: () => void;
}) {
  const { prefs } = usePreferences();
  const [systemReduced, setSystemReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const reduced = prefs.reduceMotion || systemReduced;
  const [active, setActive] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const running = !paused && !hovered && !focused && !hidden && !reduced;

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => setSystemReduced(media.matches);
    const visibility = () => setHidden(document.hidden);
    media.addEventListener('change', change);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      media.removeEventListener('change', change);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  useEffect(() => {
    if (!running) return;
    const timer = window.setTimeout(() => {
      setPrevious(active);
      setActive((active + 1) % 4);
    }, 4500);
    return () => window.clearTimeout(timer);
  }, [running, active]);
  useEffect(() => {
    if (previous === null) return;
    const timer = window.setTimeout(() => setPrevious(null), 540);
    return () => window.clearTimeout(timer);
  }, [previous, active]);
  function select(index: number) {
    if (index === active) return;
    setPrevious(active);
    setActive((index + 4) % 4);
  }
  function slide(index: number) {
    const selected = index === active;
    const leaving = index === previous && !reduced;
    const title =
      index === 0
        ? release.title
        : index === 1
          ? 'A better TruXSocial, for you'
          : index === 2
            ? 'Continuing to make TruXSocial better'
            : 'Built with you, for what’s next';
    const copy =
      index === 0
        ? release.summary
        : index === 1
          ? 'Explore what’s new and find more ways to connect in the latest release.'
          : index === 2
            ? 'Follow the progress. Explore new tools, thoughtful improvements, and the changes behind each release.'
            : 'Your ideas help shape what comes next. Share a suggestion and join the conversation.';
    const HighlightRow = index === 3 ? CommunityHighlights : Highlights;
    return (
      <Slide
        key={index}
        $leaving={leaving}
        $hidden={!selected && !leaving}
        $animate={previous !== null && !reduced && (selected || leaving)}
        inert={!selected || undefined}
        aria-hidden={!selected || undefined}
        role={selected ? 'group' : undefined}
        aria-roledescription={selected ? 'slide' : undefined}
        aria-label={selected ? `${index + 1} of 4: ${SLIDE_NAMES[index]}` : undefined}
      >
        <Metadata>
          <Latest>Latest Release</Latest>
          <strong data-testid={selected ? 'current-version' : undefined}>v{release.version}</strong>
          <p>Released {releaseDate(release.date)}</p>
          <Platforms>
            {release.platforms.map((platform) => (
              <span key={platform} data-platform={platform}>
                <PlatformIcon platform={platform} size={16} />
                {PLATFORM_LABELS[platform]}
              </span>
            ))}
          </Platforms>
        </Metadata>
        <Message>
          <h2>{title}</h2>
          <p>{copy}</p>
          {index > 0 && (
            <HighlightRow $timeline={index === 2}>
              {HIGHLIGHTS[index]!.map(([icon, label]) => (
                <span key={label}>
                  <Icon name={icon} size={21} />
                  {label}
                </span>
              ))}
            </HighlightRow>
          )}
          <ReleaseLink type="button" onClick={onViewRelease}>
            View full release notes <Icon name="share" size={17} />
          </ReleaseLink>
        </Message>
        <BannerArtwork variant={index} version={release.version} />
      </Slide>
    );
  }
  return (
    <Frame
      aria-label="Latest release highlights"
      aria-roledescription="carousel"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onTouchStart={(event) => {
        const first = event.touches[0];
        if (first) touch.current = { x: first.clientX, y: first.clientY };
      }}
      onTouchEnd={(event) => {
        const end = event.changedTouches[0];
        const start = touch.current;
        touch.current = null;
        if (!start || !end) return;
        const dx = end.clientX - start.x;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(end.clientY - start.y) * 1.5)
          select(active + (dx > 0 ? 1 : -1));
      }}
    >
      <Viewport>{SLIDE_NAMES.map((_, index) => slide(index))}</Viewport>
      <Controls>
        <span>{String(active + 1).padStart(2, '0')} / 04</span>
        <BannerControl
          type="button"
          aria-label="Previous banner"
          onClick={() => select(active - 1)}
        >
          <Icon name="chevron-left" size={16} />
        </BannerControl>
        {SLIDE_NAMES.map((name, index) => (
          <Dot
            key={name}
            type="button"
            $selected={index === active}
            aria-label={`Show banner ${index + 1}: ${name}`}
            aria-pressed={index === active}
            onClick={() => select(index)}
          />
        ))}
        <BannerControl type="button" aria-label="Next banner" onClick={() => select(active + 1)}>
          <Icon name="chevron-right" size={16} />
        </BannerControl>
        <BannerControl
          type="button"
          aria-label={
            reduced
              ? 'Automatic rotation disabled by reduced motion'
              : paused
                ? 'Play banner rotation'
                : 'Pause banner rotation'
          }
          title={reduced ? 'Reduced motion is enabled' : undefined}
          disabled={reduced}
          onClick={() => setPaused((value) => !value)}
        >
          <Icon name={paused || reduced ? 'play' : 'pause'} size={14} />
        </BannerControl>
      </Controls>
    </Frame>
  );
}
